/**
 * InvoiceList Component
 *
 * Table/card list of invoices for a project with status badges,
 * amounts, and kebab menu actions.
 *
 * @module components/invoices/InvoiceList
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Row,
  Col,
  Badge,
  Button,
  Dropdown,
  Spinner,
  Alert,
  Modal,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
  listInvoices,
  createInvoice,
  deleteInvoice,
  generateFromQuotation,
  getInvoice,
} from '../../services/invoiceService';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../common/ViewToggle';
import { DataTable } from '../common/DataTable';
import KebabMenu from '../common/KebabMenu';
import { useAuth } from '../../contexts/AuthContext';
import { exportInvoiceToPDF } from '../../utils/invoiceExport';
import InvoiceForm from './InvoiceForm';
import InvoiceDetail from './InvoiceDetail';

const statusConfig = {
  draft: { label: 'Borrador', variant: 'secondary' },
  sent: { label: 'Enviada', variant: 'primary' },
  partial_payment: { label: 'Pago Parcial', variant: 'warning' },
  paid: { label: 'Pagada', variant: 'success' },
  overdue: { label: 'Vencida', variant: 'danger' },
  cancelled: { label: 'Cancelada', variant: 'dark' },
};

const InvoiceList = ({ projectId, quotations = [] }) => {
  const queryClient = useQueryClient();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();
  const canEdit = isAdmin || isTechnician;

  const [showForm, setShowForm] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: invoicesData, isLoading, error } = useQuery({
    queryKey: ['invoices', projectId],
    queryFn: () => listInvoices({ projectId }),
    enabled: !!projectId,
  });

  const invoices = invoicesData?.data || [];

  const invoiceColumns = useMemo(() => {
    const cols = [
      {
        id: 'invoice_number',
        header: 'Numero',
        accessor: 'invoice_number',
        minWidth: 130,
        cell: (inv) => (
          <Button
            variant="link"
            className="p-0"
            onClick={(e) => { e.stopPropagation(); setSelectedInvoiceId(inv.id); }}
          >
            {inv.invoice_number}
          </Button>
        ),
      },
      {
        id: 'invoice_date',
        header: 'Fecha',
        accessor: 'invoice_date',
        minWidth: 110,
        cell: (inv) => formatDate(inv.invoice_date),
      },
      {
        id: 'status',
        header: 'Estado',
        accessor: 'status',
        minWidth: 110,
        cell: (inv) => {
          const si = statusConfig[inv.status] || statusConfig.draft;
          return <Badge bg={si.variant}>{si.label}</Badge>;
        },
      },
      {
        id: 'amount',
        header: 'Monto',
        accessor: (inv) => inv.amount || 0,
        align: 'right',
        minWidth: 110,
        cell: (inv) => formatCurrency(inv.amount, inv.currency),
      },
      {
        id: 'paid',
        header: 'Pagado',
        accessor: (inv) => inv.paid_amount || 0,
        align: 'right',
        minWidth: 110,
        cell: (inv) => <span className="text-success">{formatCurrency(inv.paid_amount, inv.currency)}</span>,
      },
      {
        id: 'balance',
        header: 'Pendiente',
        accessor: (inv) => inv.balance || 0,
        align: 'right',
        minWidth: 110,
        cell: (inv) => <span className="text-danger">{formatCurrency(inv.balance, inv.currency)}</span>,
      },
      {
        id: 'due_date',
        header: 'Vencimiento',
        accessor: 'due_date',
        minWidth: 110,
        cell: (inv) => formatDate(inv.due_date),
      },
    ];

    if (canEdit) {
      cols.push({
        id: 'actions',
        header: '',
        sortable: false,
        resizable: false,
        align: 'center',
        noSelect: true,
        minWidth: 50,
        maxWidth: 60,
        cell: (inv) => (
          <div data-no-select onClick={(e) => e.stopPropagation()}>
            <KebabMenu label={`Acciones de la factura ${inv.invoice_number}`}>
              <Dropdown.Item onClick={() => setSelectedInvoiceId(inv.id)}>
                <i className="bi bi-eye me-2"></i>Ver
              </Dropdown.Item>
              {inv.status === 'draft' && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    className="text-danger"
                    onClick={() => {
                      setInvoiceToDelete(inv);
                      setShowDeleteModal(true);
                    }}
                  >
                    <i className="bi bi-trash me-2"></i>Eliminar
                  </Dropdown.Item>
                </>
              )}
            </KebabMenu>
          </div>
        ),
      });
    }
    return cols;
  }, [canEdit]);

  const handleCreateInvoice = async (data) => {
    try {
      setSaving(true);
      let result;

      if (data.type === 'from_quotation') {
        result = await generateFromQuotation(data.project_id, data.quotation_id, {
          invoice_date: data.invoice_date,
          payment_terms: data.payment_terms,
          notes: data.notes,
        });
      } else {
        result = await createInvoice(data.invoice_data, data.items);
      }

      if (result.error) throw result.error;

      toast.success('Factura creada exitosamente');
      setShowForm(false);
      queryClient.invalidateQueries(['invoices', projectId]);
    } catch (err) {
      console.error('Create invoice error:', err);
      // #629 — gemelo de InvoiceListPage.js. Ver el comentario de allá: las
      // compuertas de facturación dicen qué falta y ese texto tiene que
      // llegar a la pantalla, no morir en un toast genérico.
      toast.error(err?.message || 'Error al crear la factura');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePDF = async (invoice) => {
    try {
      const { data: full, error: pdfError } = await getInvoice(invoice.id);
      if (pdfError) throw pdfError;
      await exportInvoiceToPDF(full);
      toast.success('PDF generado');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Error al generar PDF');
    }
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    try {
      const result = await deleteInvoice(invoiceToDelete.id);
      if (result.error) throw result.error;
      toast.success('Factura eliminada');
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      queryClient.invalidateQueries(['invoices', projectId]);
    } catch (err) {
      toast.error('Error al eliminar factura');
    }
  };

  // Show invoice detail view
  if (selectedInvoiceId) {
    return (
      <InvoiceDetail
        invoiceId={selectedInvoiceId}
        onBack={() => setSelectedInvoiceId(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <span className="ms-2">Cargando facturas...</span>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">Error al cargar facturas: {error.message}</Alert>;
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-receipt-cutoff me-2"></i>
            Facturas del Proyecto
          </h5>
          <div className="d-flex gap-2">
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
            {canEdit && (
              <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
                <i className="bi bi-plus-circle me-1"></i>
                Nueva Factura
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {invoices.length === 0 ? (
            <p className="text-muted text-center py-4">
              No hay facturas para este proyecto.
            </p>
          ) : isTableView ? (
            <DataTable
              tableId="invoices"
              data={invoices}
              rowKey={(inv) => inv.id}
              onRowClick={(inv) => setSelectedInvoiceId(inv.id)}
              columns={invoiceColumns}
            />
          ) : (
            <Row className="g-3">
              {invoices.map((inv) => {
                const si = statusConfig[inv.status] || statusConfig.draft;
                return (
                  <Col xs={12} sm={6} lg={4} key={inv.id}>
                    <Card className="shadow-sm h-100">
                      <Card.Body style={{ position: 'relative' }}>
                        {canEdit && (
                          <KebabMenu
                            label={`Acciones de la factura ${inv.invoice_number}`}
                            stopPropagation={false}
                            dropdownProps={{ style: { position: 'absolute', top: '12px', right: '12px' } }}
                          >
                            <Dropdown.Item onClick={() => setSelectedInvoiceId(inv.id)}>
                              <i className="bi bi-eye me-2"></i>Ver
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleGeneratePDF(inv)}>
                              <i className="bi bi-file-earmark-pdf me-2"></i>Generar PDF
                            </Dropdown.Item>
                            {inv.status === 'draft' && (
                              <>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  className="text-danger"
                                  onClick={() => {
                                    setInvoiceToDelete(inv);
                                    setShowDeleteModal(true);
                                  }}
                                >
                                  <i className="bi bi-trash me-2"></i>Eliminar
                                </Dropdown.Item>
                              </>
                            )}
                          </KebabMenu>
                        )}

                        <div className="mb-2 pe-4">
                          <Button
                            variant="link"
                            className="p-0 fw-bold"
                            onClick={() => setSelectedInvoiceId(inv.id)}
                          >
                            {inv.invoice_number}
                          </Button>
                        </div>

                        <Badge bg={si.variant} className="mb-2">{si.label}</Badge>

                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-primary fw-bold fs-5">{formatCurrency(inv.amount, inv.currency)}</span>
                        </div>

                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-success">Pagado: {formatCurrency(inv.paid_amount, inv.currency)}</span>
                          <span className="text-danger">Saldo: {formatCurrency(inv.balance, inv.currency)}</span>
                        </div>

                        <div className="d-flex justify-content-between small mt-2 pt-2 border-top">
                          <span className="text-muted">
                            <i className="bi bi-calendar3 me-1"></i>
                            {formatDate(inv.invoice_date)}
                          </span>
                          <span className="text-muted">
                            Vence: {formatDate(inv.due_date)}
                          </span>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Invoice Form Modal */}
      <InvoiceForm
        show={showForm}
        onHide={() => setShowForm(false)}
        onSave={handleCreateInvoice}
        projectId={projectId}
        quotations={quotations}
        loading={saving}
      />

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminacion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Estas seguro de eliminar la factura <strong>{invoiceToDelete?.invoice_number}</strong>?</p>
          <p className="text-danger mb-0">
            <i className="bi bi-exclamation-triangle me-1"></i>
            Esta accion no se puede deshacer.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <i className="bi bi-trash me-1"></i>Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InvoiceList;
